import { tournamentSettingsSchema, type TournamentSettings } from '@/core/schemas';

/**
 * The starting configuration for a fresh tournament.
 *
 * Only the four fields the admin picks in the create form are parameters. The
 * rest — phases, match rules, game settings — are sensible defaults for the
 * FF-Ratekau format and stay editable under `Admin → Einstellungen` before
 * the start. The CLI script `scripts/create-tournament.mjs` mirrors these
 * defaults; keep the two in sync.
 *
 * Runs through {@link tournamentSettingsSchema} so branded ids (PhaseId,
 * FormatId, RoundKey) are minted through the same path as any other write.
 */
export function defaultTournamentSettings(input: {
  name: string;
  date: string;
  boardCount: number;
  maxTeams: number;
}): TournamentSettings {
  return tournamentSettingsSchema.parse({
    general: {
      name: input.name,
      date: input.date,
      boardCount: input.boardCount,
      maxTeams: input.maxTeams,
    },
    registration: {
      isOpen: false,
      songMaxDurationSeconds: 30,
      allowMultipleTeamsPerFireDepartment: true,
    },
    game: { startScore: 501, doubleOut: true },
    phases: [
      {
        id: 'gruppenphase',
        label: 'Gruppenphase',
        formatId: 'group-stage',
        config: {
          groupCount: 2,
          distribution: 'snake',
          doubleRound: false,
          qualifiersPerGroup: 2,
          additionalQualifiers: 0,
          pointsForWin: 3,
          pointsForDraw: 1,
          pointsForLoss: 0,
          tiebreakers: ['points', 'legDifference', 'legsFor', 'headToHead'],
        },
      },
      {
        id: 'ko',
        label: 'KO-Phase',
        formatId: 'single-elimination',
        config: {
          bracketSize: 'auto',
          seeding: 'standard',
          autoByes: true,
          thirdPlaceMatch: true,
        },
      },
    ],
    matchRules: {
      default: { scoring: 'legs', bestOf: 3 },
      byPhase: { ko: { scoring: 'legs', bestOf: 5 } },
      byRound: {
        'ko.halbfinale': { scoring: 'legs', bestOf: 7 },
        'ko.finale': { scoring: 'legs', bestOf: 9 },
      },
    },
  });
}
